import { sellablePermitRates } from '@/lib/permit-rate-filter';
import type { GarageResult } from '@/types/reserve.types';

/**
 * Whether this garage carries a rate exempt from the multiple-permit cap.
 *
 * ## Invariants:
 * - Reads the coupon-revealed rates as well as the public ones (PB-5582). "A second permit for
 *   existing parkers, by code" is a plausible operator setup and exactly what coupon-limited
 *   inventory is for — so for a customer who already holds a permit, the exempt rate can be the
 *   revealed one. Reading only `permitRates` skipped the whole permit block for that customer:
 *   the revealed rows were never built while the applied chip still reported the server's count,
 *   so the headline and the page disagreed with nothing logging it.
 * - Resolves inventory through `sellablePermitRates`, not an inline spread of the same two
 *   fields. The inline form was a second copy of the class's one rule, invisible to the drift
 *   guard, and a third field would have had to be remembered here separately.
 */
export const garageHasExemptRate = (garage: GarageResult): boolean =>
  sellablePermitRates(garage).some((r) => r.ignoreRateFromMaxCountForUser);

/**
 * Reserve-store selector: monthly mode is reachable when the user can book a
 * new permit, or any nearby garage offers an exempt rate — legacy
 * enableMonthlyTab keeps the Monthly tab for exactly that case (the user may
 * still book the exempt rate at their limit).
 */
export const canAccessMonthlyMode = (state: {
  canBookNewPermit: boolean;
  garageResults: GarageResult[];
}): boolean =>
  state.canBookNewPermit || (state.garageResults ?? []).some(garageHasExemptRate);

// Only an explicit false gates, matching the store's fail-open default.
export const isRateBookableAtPermitLimit = (
  rate: { ignoreRateFromMaxCountForUser?: boolean } | null | undefined,
  canBookNewPermit: boolean | undefined
): boolean => canBookNewPermit !== false || !!rate?.ignoreRateFromMaxCountForUser;

export const filterRatesForPermitLimit = <T extends { ignoreRateFromMaxCountForUser?: boolean }>(
  rates: T[],
  canBookNewPermit: boolean | undefined
): T[] => rates.filter((r) => isRateBookableAtPermitLimit(r, canBookNewPermit));
